import React, { useState, useEffect, useRef } from 'react';
import API_CONFIG from '../../apiConfig';
import CustomSelect from '../ui/CustomSelect';

interface ModelSelectionProps {
  selectedModelId: string | null;
  setSelectedModelId: React.Dispatch<React.SetStateAction<string | null>>;
  onModelSelect?: (modelUrl: string, modelId: string) => void;
  disabled?: boolean;
  genderFilter: string;
  onGenderFilterChange: (gender: string) => void;
}

interface Model {
  modelId: string;
  imageUrl: string[];
  prompt: string;
  type: string;
  gender: string;
  modelName: string;
  region: string;
}

const ModelSelection: React.FC<ModelSelectionProps> = ({ 
  selectedModelId, 
  setSelectedModelId, 
  onModelSelect,
  disabled = false,
  genderFilter,
  onGenderFilterChange
}) => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_CONFIG.GET_TRYON_MODELS_URL);

        // Check HTTP status
        if (!response.ok) {
          setError('Something went wrong. Please try again.');
          return;
        }

        const data = await response.json();
        
        // Check if models were returned
        if (data.success && data.models && data.models.length > 0) {
          // Sort models alphabetically by model name
          const sortedModels = data.models.sort((a: Model, b: Model) => {
            const nameA = a.modelName || `${a.type} ${a.gender}`;
            const nameB = b.modelName || `${b.type} ${b.gender}`;
            return nameA.localeCompare(nameB);
          });
          setModels(sortedModels);
        } else {
          setError('Failed to load models');
        }
      } catch (error) {
        console.error('Error fetching models:', error);
        setError('Failed to load models');
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleModelSelect = (model: Model) => {
    const imageUrl = Array.isArray(model.imageUrl) ? model.imageUrl[0] : model.imageUrl;
    // Clean up the URL by removing extra quotes if they exist
    const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
    
    setSelectedModelId(model.modelId);
    setIsDropdownOpen(false);
    setIsCollapsed(true); // Collapse after selection
    if (onModelSelect) {
      onModelSelect(cleanImageUrl, model.modelId);
    }
  };

  const getModelDisplayName = (model: Model) => {
    return model.modelName || `${model.type} ${model.gender}`;
  };

  // Filter models based on gender selection
  const filteredModels = models.filter(model => model.gender.toLowerCase() === genderFilter.toLowerCase());

  const selectedModel = models.find(m => m.modelId === selectedModelId);

  const handleGenderFilterChange = (gender: string) => {
    onGenderFilterChange(gender);
    
    // If current selected model doesn't match the filter, clear selection
    if (selectedModel && selectedModel.gender.toLowerCase() !== gender.toLowerCase()) {
      setSelectedModelId(null);
      if (onModelSelect) {
        onModelSelect('', '');
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-accent-sand rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-text-heading">Models</h3>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
            <span className="text-text-description">Loading models...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-accent-sand rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-text-heading">Models</h3>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-red-500 text-2xl mb-2"></i>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show collapsed view if a model is selected and component is collapsed (desktop only)
  if (isCollapsed && selectedModelId) {
    const selectedModel = models.find(m => m.modelId === selectedModelId);
    
    return (
      <div className={`bg-accent-sand rounded-lg p-4 relative ${disabled ? 'opacity-50' : ''}`}>
        {/* Desktop collapsed view */}
        <div className="hidden md:block">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-text-heading">Models</h3>
            <button
              onClick={() => setIsCollapsed(false)}
              className="text-secondary-600 hover:text-secondary-800 text-sm font-medium"
            >
              Change
            </button>
          </div>
          
          <div className="mt-3 p-3 bg-white rounded-lg border border-accent-taupe">
            <div className="flex items-center space-x-3">
              {selectedModelId === 'random' ? (
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <i className="fas fa-random text-secondary-600"></i>
                </div>
              ) : selectedModel ? (
                <img
                  src={(() => {
                    const imageUrl = Array.isArray(selectedModel.imageUrl) ? selectedModel.imageUrl[0] : selectedModel.imageUrl;
                    return typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                  })()}
                  alt={selectedModel.modelName || `Model ${selectedModel.modelId}`}
                  className="w-12 h-12 object-contain rounded-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                  }}
                />
              ) : null}
              
              <div className="flex-1">
                <p className="text-sm font-medium text-text-heading">
                  {selectedModelId === 'random' 
                    ? 'Random Model' 
                    : selectedModel 
                      ? getModelDisplayName(selectedModel)
                      : 'Selected Model'
                  }
                </p>
                <p className="text-xs text-text-description">
                  {selectedModelId === 'random' 
                    ? 'System will choose automatically'
                    : selectedModel 
                      ? `${selectedModel.gender} - ${selectedModel.region}`
                      : 'Model selected'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile view - always show full component */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-text-heading">Models</h3>
            
            {/* Gender Filter Dropdown */}
            <div className="relative">
              <CustomSelect
                value={genderFilter}
                onChange={(value) => handleGenderFilterChange(value)}
                options={[
                  { value: 'male', label: 'Male Models' },
                  { value: 'female', label: 'Female Models' }
                ]}
                className="w-full"
              />
            </div>
          </div>
          
          {/* Mobile custom dropdown */}
          <div className="relative mb-4" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 bg-white text-left flex items-center justify-between"
            >
              <span className="truncate">
                {selectedModel ? getModelDisplayName(selectedModel) : selectedModelId === 'random' ? 'Random Model' : 'Choose a model...'}
              </span>
              <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'} text-text-description ml-2`}></i>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-accent-taupe rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {/* Random Model option */}
                <button
                  onClick={() => {
                    setSelectedModelId('random');
                    setIsDropdownOpen(false);
                    if (onModelSelect) {
                      onModelSelect('', 'random');
                    }
                  }}
                  className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe"
                >
                  <div className="w-12 h-12 bg-secondary-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                    <i className="fas fa-random text-secondary-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-heading truncate">
                      Random Model
                    </p>
                    <p className="text-xs text-text-description truncate">
                      System will choose automatically
                    </p>
                  </div>
                  {selectedModelId === 'random' && (
                    <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                  )}
                </button>
                
                {filteredModels.map((model) => {
                  const imageUrl = Array.isArray(model.imageUrl) ? model.imageUrl[0] : model.imageUrl;
                  const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                  
                  return (
                    <button
                      key={model.modelId}
                      onClick={() => {
                        const imageUrl = Array.isArray(model.imageUrl) ? model.imageUrl[0] : model.imageUrl;
                        const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                        
                        setSelectedModelId(model.modelId);
                        setIsDropdownOpen(false);
                        if (onModelSelect) {
                          onModelSelect(cleanImageUrl, model.modelId);
                        }
                      }}
                      className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe last:border-b-0"
                    >
                      <img
                        src={cleanImageUrl}
                        alt={model.modelName || `Model ${model.modelId}`}
                        className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-heading truncate">
                          {getModelDisplayName(model)}
                        </p>
                        <p className="text-xs text-text-description truncate">
                          {model.gender}
                        </p>
                      </div>
                      {selectedModelId === model.modelId && (
                        <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Mobile model preview */}
          {selectedModelId && selectedModelId !== 'random' && selectedModel && (
            <div className="space-y-3">
              <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
                <img
                  src={(() => {
                    const imageUrl = Array.isArray(selectedModel.imageUrl) ? selectedModel.imageUrl[0] : selectedModel.imageUrl;
                    return typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                  })()}
                  alt={selectedModel.modelName || `Model ${selectedModel.modelId}`}
                  className="w-full h-64 object-contain bg-accent-sand"
                  onError={(e) => {
                    console.error('Image failed to load');
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=Model+Not+Available';
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                  <p className="text-sm font-medium text-white text-center">
                    {getModelDisplayName(selectedModel)}
                  </p>
                </div>
              </div>
              
              <div className="p-2 bg-secondary-100 rounded-lg">
                <p className="text-sm text-secondary-700">
                  <i className="fas fa-info-circle mr-1"></i>
                  Selected: {getModelDisplayName(selectedModel)} - {selectedModel.region} {selectedModel.gender}
                </p>
              </div>
            </div>
          )}

          {/* Random model selection info */}
          {selectedModelId === 'random' && (
            <div className="space-y-3">
              <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
                <div className="w-full h-64 bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
                  <div className="text-center">
                    <i className="fas fa-random text-4xl text-secondary-600 mb-4"></i>
                    <p className="text-lg font-medium text-text-heading">Random Model</p>
                    <p className="text-sm text-text-description">System will choose automatically</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2 bg-secondary-100 rounded-lg">
                <p className="text-sm text-secondary-700">
                  <i className="fas fa-info-circle mr-1"></i>
                  Selected: Random Model - System will choose automatically
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-accent-sand rounded-lg p-4 relative flex flex-col h-full ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h3 className="text-lg font-medium text-text-heading">Models</h3>
        
                {/* Gender Filter Dropdown */}
        <div className="relative">
          <CustomSelect
            value={genderFilter}
            onChange={(value) => handleGenderFilterChange(value)}
            options={[
              { value: 'male', label: 'Male Models' },
              { value: 'female', label: 'Female Models' }
            ]}
            className="w-full"
          />
        </div>
      </div>
      
      {disabled && (
        <div className="absolute inset-0 bg-accent-sand bg-opacity-90 rounded-lg flex items-center justify-center z-10">
          <div className="text-center p-4">
            <i className="fas fa-lock text-2xl text-text-description mb-2"></i>
            <p className="text-sm font-medium text-text-heading mb-1">Model Selection Disabled</p>
            <p className="text-xs text-text-description">Clear the prompt text from advance settings to enable model selection</p>
          </div>
        </div>
      )}
      
      {/* Mobile custom dropdown - shown only on screens smaller than md */}
      <div className="md:hidden">
        <div className="relative mb-4" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 bg-white text-left flex items-center justify-between"
          >
            <span className="truncate">
              {selectedModel ? getModelDisplayName(selectedModel) : selectedModelId === 'random' ? 'Random Model' : 'Choose a model...'}
            </span>
            <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'} text-text-description ml-2`}></i>
          </button>
          
          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-accent-taupe rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {/* Random Model option */}
              <button
                onClick={() => {
                  setSelectedModelId('random');
                  setIsDropdownOpen(false);
                  setIsCollapsed(true); // Collapse after selection
                  if (onModelSelect) {
                    onModelSelect('', 'random');
                  }
                }}
                className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe"
              >
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                  <i className="fas fa-random text-secondary-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-heading truncate">
                    Random Model
                  </p>
                  <p className="text-xs text-text-description truncate">
                    System will choose automatically
                  </p>
                </div>
                {selectedModelId === 'random' && (
                  <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                )}
              </button>
              
              {filteredModels.map((model) => {
                const imageUrl = Array.isArray(model.imageUrl) ? model.imageUrl[0] : model.imageUrl;
                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                
                return (
                  <button
                    key={model.modelId}
                    onClick={() => handleModelSelect(model)}
                    className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe last:border-b-0"
                  >
                    <img
                      src={cleanImageUrl}
                      alt={model.modelName || `Model ${model.modelId}`}
                      className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-heading truncate">
                        {getModelDisplayName(model)}
                      </p>
                      <p className="text-xs text-text-description truncate">
                        {model.gender}
                      </p>
                    </div>
                    {selectedModelId === model.modelId && (
                      <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Mobile model preview */}
        {selectedModelId && selectedModelId !== 'random' && selectedModel && (
          <div className="space-y-3">
            <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
              <img
                src={(() => {
                  const imageUrl = Array.isArray(selectedModel.imageUrl) ? selectedModel.imageUrl[0] : selectedModel.imageUrl;
                  return typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                })()}
                alt={selectedModel.modelName || `Model ${selectedModel.modelId}`}
                className="w-full h-64 object-contain bg-accent-sand"
                onError={(e) => {
                  console.error('Image failed to load');
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=Model+Not+Available';
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                <p className="text-sm font-medium text-white text-center">
                  {getModelDisplayName(selectedModel)}
                </p>
              </div>
            </div>
            
            <div className="p-2 bg-secondary-100 rounded-lg">
              <p className="text-sm text-secondary-700">
                <i className="fas fa-info-circle mr-1"></i>
                Selected: {getModelDisplayName(selectedModel)} - {selectedModel.region} {selectedModel.gender}
              </p>
            </div>
          </div>
        )}

        {/* Random model selection info */}
        {selectedModelId === 'random' && (
          <div className="space-y-3">
            <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
              <div className="w-full h-64 bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
                <div className="text-center">
                  <i className="fas fa-random text-4xl text-secondary-600 mb-4"></i>
                  <p className="text-lg font-medium text-text-heading">Random Model</p>
                  <p className="text-sm text-text-description">System will choose automatically</p>
                </div>
              </div>
            </div>
            
            <div className="p-2 bg-secondary-100 rounded-lg">
              <p className="text-sm text-secondary-700">
                <i className="fas fa-info-circle mr-1"></i>
                Selected: Random Model - System will choose automatically
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Desktop grid - shown only on md screens and larger */}
      <div className="hidden md:flex flex-1 min-h-0 flex-col">
        <div className="flex-1 overflow-y-auto pr-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 pl-2 pb-2">
          {/* Random Model card */}
          <button
            onClick={() => {
              setSelectedModelId('random');
              setIsCollapsed(true); // Collapse after selection
              if (onModelSelect) {
                onModelSelect('', 'random');
              }
            }}
            className={`group relative rounded-lg overflow-hidden transition-all duration-200 ${
              selectedModelId === 'random'
                ? 'ring-4 ring-secondary-500 ring-opacity-50 shadow-lg' 
                : 'hover:shadow-md hover:scale-105'
            }`}
          >
            <div className="w-full h-32 lg:h-28 xl:h-40 bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
              <div className="text-center">
                <i className="fas fa-random text-xl lg:text-lg xl:text-2xl text-secondary-600 mb-2"></i>
                <p className="text-xs lg:text-xs xl:text-sm font-medium text-text-heading">Random</p>
              </div>
            </div>
            <div className={`absolute inset-0 transition-opacity ${
              selectedModelId === 'random'
                ? 'bg-secondary-500 bg-opacity-20' 
                : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
            }`} />
            {selectedModelId === 'random' && (
              <div className="absolute top-2 right-2">
                <div className="bg-secondary-500 text-white rounded-full p-1">
                  <i className="fas fa-check text-xs"></i>
                </div>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 lg:p-1.5 xl:p-3">
              <p className="text-xs lg:text-xs xl:text-sm font-medium text-white text-center leading-tight">
                Random Model
              </p>
            </div>
          </button>
          
          {filteredModels.map((model) => {
            const imageUrl = Array.isArray(model.imageUrl) ? model.imageUrl[0] : model.imageUrl;
            const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
            const isSelected = selectedModelId === model.modelId;
            
            return (
              <button
                key={model.modelId}
                onClick={() => handleModelSelect(model)}
                className={`group relative rounded-lg overflow-hidden transition-all duration-200 ${
                  isSelected 
                    ? 'ring-4 ring-secondary-500 ring-opacity-50 shadow-lg' 
                    : 'hover:shadow-md hover:scale-105'
                }`}
              >
                <img
                  src={cleanImageUrl}
                  alt={model.modelName || `Model ${model.modelId}`}
                  className="w-full h-32 lg:h-28 xl:h-40 object-contain"
                  onError={(e) => {
                    console.error('Image failed to load:', cleanImageUrl);
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150x200?text=Model+Not+Available';
                  }}
                />
                <div className={`absolute inset-0 transition-opacity ${
                  isSelected 
                    ? 'bg-secondary-500 bg-opacity-20' 
                    : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
                }`} />
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-secondary-500 text-white rounded-full p-1">
                      <i className="fas fa-check text-xs"></i>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 lg:p-1.5 xl:p-3">
                  <p className="text-xs lg:text-xs xl:text-sm font-medium text-white text-center leading-tight">
                    {getModelDisplayName(model)}
                  </p>
                </div>
              </button>
            );
          })}
          </div>
        </div>
        
        {/* Desktop selection info */}
        {selectedModelId && (
          <div className="mt-3 p-2 bg-secondary-100 rounded-lg flex-shrink-0">
            <p className="text-sm text-secondary-700">
              <i className="fas fa-info-circle mr-1"></i>
              {selectedModelId === 'random' 
                ? 'Model selected: Random Model - System will choose automatically'
                : selectedModel 
                  ? `Model selected: ${getModelDisplayName(selectedModel)} - ${selectedModel.region} ${selectedModel.gender}`
                  : 'Model selected'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelSelection; 